import React, { useState, useEffect } from 'react';
import {
  styled,
  Button,
  TextField,
  Tooltip,
  Container,
  Typography,
  InputAdornment,
  Box,
  Table,
  Modal,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Close as CloseIcon,
  KeyboardDoubleArrowLeft as ChevronLeft,
  KeyboardDoubleArrowRight as ChevronRight,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';
import SearchIcon from '@mui/icons-material/Search';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import { Navigate, useNavigate } from 'react-router-dom';
import { useBranch } from '../../../layouts/BranchContext'; 

const CustomTableScrollbar = styled('div')(({ theme }) => ({
  '&::-webkit-scrollbar': {
    height: '6px',
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)',
  },
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} transparent`,
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const User = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const rowsPerPageOptions = [5, 10, 25, 50];
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showUserGuideModal, setShowUserGuideModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalType, setSuccessModalType] = useState(''); // 'add' or 'edit'
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMatchError, setPasswordMatchError] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({});
  const [roleList, setRoleList] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    userRole: '',
    employeeName: '',
    userName: '',
    password: '',
    confirmPassword: '',
    expiryDate: '',
  });

  // ── Branch filter state — mirrors Course.js pattern ───────────────────────
  const [allUsers, setAllUsers] = useState([]);
 

  const apiUrl = process.env.REACT_APP_API_BASE_URL || 'https://wims.revisetoexcel.com';

  // ── Parse stored user ────────────────────────────────────────────────────
  const getStoredUser = () => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser || storedUser === 'undefined') return {};
    try {
      return JSON.parse(storedUser);
    } catch (err) {
      console.warn('Invalid user data in localStorage:', err, storedUser);
      return {};
    }
  };

   const { selectedBranchId, branchList, isMainBranchAdmin } = useBranch();

const loadAllData = async (branchId = '') => {
  const accessToken = localStorage.getItem('accessToken');
  if (!accessToken) {
    setSnackbarMessage('Authentication token missing');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
    return;
  }

  const headers = {
    'Authorization': `Bearer ${accessToken}`,
  };

  try {
    // Fetch users
    const usersRes = await fetch(
  `${apiUrl}/api/staff/users/${branchId ? `?branch=${branchId}` : ''}`,
  { headers }
);

    // Users
    if (usersRes.ok) {
      const data = await usersRes.json();
      const mappedData = data.map(item => ({
        UserId: item.id || item.UserId || 0,
        UserRoleName: item.role_name || item.role || 'Unknown',
        student_name: item.student_name  || 'Unknown',
        EmployeeName: item.employee_name || item.student_name || 'Unknown',
        UserName: item.username || item.email || item.UserName || 'Unknown',
        Password: item.raw_password_store || '******', 
        ExpiryDate: item.expiry_date || item.expiry || item.ExpiryDate || '',
        branch: item.branch || item.branch_id || null,
        organization: item.organization || item.organization_id || null,
      }));
      // Store full list in allUsers — branch filter effect derives users + filteredData
      setAllUsers(mappedData);
setUsers(mappedData);  // ← ADD THIS
setPage(1); 
      // Populate roleList from unique roles in data
      const uniqueRoles = [...new Set(mappedData.map(u => u.UserRoleName).filter(r => r !== 'Unknown'))];
      setRoleList(uniqueRoles.map(name => ({ id: name, name })));
    } else {
      setSnackbarMessage('Failed to load users');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  } catch (err) {
    setSnackbarMessage('Error loading data');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
  }
};

useEffect(() => {
  loadAllData(selectedBranchId);
}, [selectedBranchId]);

const [users, setUsers] = useState([]);
const [filteredData, setFilteredData] = useState([]);

  // ── Frontend branch filter — mirrors Course.js pattern ───────────────────
  // Whenever selectedBranchId or allUsers changes, filter in memory without re-fetch
  
 const togglePasswordVisibility = (userId) => {
  setPasswordVisibility((prev) => ({
    ...prev,
    [userId]: !prev[userId],
  }));
};

  const handleAddUser = () => {
    setFormData({
      userRole: '',
      employeeName: '',
      userName: '',
      password: '',
      confirmPassword: '',
      expiryDate: '',
    });
    setPasswordMatchError(false);
    setShowAddUserModal(true);
  };

  const handleEdit = (user) => {
    setSelectedUser(user);
    setFormData({
      userRole: user.UserRoleName,
      employeeName: user.EmployeeName,
      userName: user.UserName,
      password: user.Password,
      confirmPassword: user.Password,
      expiryDate: user.ExpiryDate,
    });
    setPasswordMatchError(false);
    setShowEditUserModal(true);
  };

  const handleShowDeleteModal = (user) => {
    setSelectedUser(user); 
    setShowDeleteModal(true);
  };

const handleDelete = async () => {
  setDeleting(true);
  const accessToken = localStorage.getItem('accessToken');

  try {
    const response = await fetch(`${apiUrl}/api/staff/users/${selectedUser.UserId}/`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    });

    if (response.ok || response.status === 204) { // 204 No Content is common for DELETE
      await loadAllData(selectedBranchId); // Refresh all data from server
      setSnackbarMessage(`${selectedUser.EmployeeName} deleted successfully!`);
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setShowDeleteModal(false);
      setSelectedUser(null);
    } else {
      const err = await response.json().catch(() => ({}));
      setSnackbarMessage(err.detail || err.message || 'Failed to delete user');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  } catch (err) {
    setSnackbarMessage('Error deleting user');
    setSnackbarSeverity('error');
    setOpenSnackbar(true);
  } finally {
    setDeleting(false);
  }
};

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedUser(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (
      formData.userRole &&
      formData.employeeName &&
      formData.userName &&
      formData.password &&
      formData.confirmPassword &&
      formData.expiryDate
    ) {
      if (formData.password !== formData.confirmPassword) {
        setPasswordMatchError(true);
        return;
      }
      const newUser = {
        UserId: users.length + 1,
        UserRoleName: formData.userRole,
        EmployeeName: formData.employeeName,
        UserName: formData.userName,
        Password: formData.password,
        ExpiryDate: formData.expiryDate,
      };
      setUsers([...users, newUser]);
      setShowAddUserModal(false);
      setSuccessModalType('add');
      setShowSuccessModal(true);
      setFormData({
        userRole: '',
        employeeName: '',
        userName: '',
        password: '',
        confirmPassword: '',
        expiryDate: '',
      });
      setPasswordMatchError(false);
    } else {
     setSnackbarMessage('Please fill all required fields!');  
  setOpenSnackbar(true);  
  setSnackbarSeverity("error");
    }
  };

  const handleUpdate = (e) => {
    e.preventDefault();
    if (
      formData.userRole &&
      formData.employeeName &&
      formData.userName &&
      formData.password &&
      formData.confirmPassword &&
      formData.expiryDate
    ) {
      if (formData.password !== formData.confirmPassword) {
        setPasswordMatchError(true);
        return;
      }
      const updatedUsers = users.map((user) =>
        user.UserId === selectedUser.UserId
          ? {
            ...user,
            UserRoleName: formData.userRole,
            EmployeeName: formData.employeeName,
            UserName: formData.userName,
            Password: formData.password,
            ExpiryDate: formData.expiryDate,
          }
          : user
      );
      setUsers(updatedUsers);
      setShowEditUserModal(false);
      setSuccessModalType('edit');
      setShowSuccessModal(true);
      setFormData({
        userRole: '',
        employeeName: '',
        userName: '',
        password: '',
        confirmPassword: '',
        expiryDate: '',
      });
      setPasswordMatchError(false);
      setSelectedUser(null);
    } else {
      setSnackbarMessage('plz fill rquired field!');  
  setOpenSnackbar(true);  
  setSnackbarSeverity("success");
    }
  };

  const handleReset = () => {
    setFormData({
      userRole: '',
      employeeName: '',
      userName: '',
      password: '',
      confirmPassword: '',
      expiryDate: '',
    });
    setPasswordMatchError(false);
  };

  const handleCancel = () => {
    setShowEditUserModal(false);
    setFormData({
      userRole: '',
      employeeName: '',
      userName: '',
      password: '',
      confirmPassword: '',
      expiryDate: '',
    });
    setPasswordMatchError(false);
    setSelectedUser(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

useEffect(() => {
  const term = searchTerm.toLowerCase();
  setFilteredData(
    users.filter((user) => {
      const role = String(user.UserRoleName || '').toLowerCase();
      const emp = String(user.EmployeeName || '').toLowerCase();
      const username = String(user.UserName || '').toLowerCase();
      const password = String(user.Password || '').toLowerCase();
      const expiry = String(user.ExpiryDate || '').toLowerCase();

      return (
        role.includes(term) ||
        emp.includes(term) ||
        username.includes(term) ||
        password.includes(term) ||
        expiry.includes(term)
      );
    })
  );
}, [searchTerm, users]);

  const paginatedData = filteredData.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <Container>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'space-between',
          mb: 1,
          p: 2,
          boxShadow: 'none',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{display:'flex', alignItems:'center', flexGrow:1,gap:2}}>
     <TextField
  variant="outlined"
  placeholder="Search..."
  value={searchTerm}
  onChange={(e) => setSearchTerm(e.target.value)}
  InputProps={{
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon
          sx={{
            color: theme.palette.primary.main,
            marginLeft: 1.5,
            fontSize: 24,
          }}
        />
      </InputAdornment>
    ),
    endAdornment: searchTerm && (
      <InputAdornment position="end">
        <IconButton
          size="small"
          onClick={() => setSearchTerm('')}
          sx={{
            color: "grey",
            marginRight: 1,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </InputAdornment>
    ),
    sx: {
      borderRadius: '30px',
      backgroundColor: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(255, 255, 255, 1)',
      boxShadow: theme.palette.mode === 'dark'
        ? '0 2px 8px rgba(0, 0, 0, 0.3)'
        : '0 2px 8px rgba(0, 0, 0, 0.1)',
      '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.1)'}`,
      },
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(240, 240, 240, 1)',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 2px 12px rgba(0, 0, 0, 0.4)'
          : '0 2px 12px rgba(0, 0, 0, 0.15)',
      },
      '&.Mui-focused': {
        backgroundColor: theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.25)'
          : 'rgba(255, 255, 255, 1)',
        boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
        '& .MuiOutlinedInput-notchedOutline': {
          border: `2px solid ${theme.palette.primary.main}`,
        },
      },
      paddingLeft: 1.5,
      height: '50px',
      transition: 'all 0.3s ease',
    },
  }}
  sx={{
    width: 350,
    maxWidth: '100%',
    '& .MuiInputBase-input': {
      padding: '10px 14px',
      color: theme.palette.text.primary,
      fontWeight: 500,
    },
  }}
/>

        <Button
          variant="contained"
          onClick={() => { navigate("/add-user") }}
          sx={{
            ml: 2,
            width: '160px',
            height: '50px',
            borderRadius: '10px',
            padding: '10px 0',
            fontWeight: 'bold',
            backgroundColor: theme.palette.primary.main,
            '&:hover': { backgroundColor: theme.palette.primary.dark },
          }}
          startIcon={<AddIcon />}
        >
          ADD USER
        </Button>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <IconButton
            onClick={() => setShowUserGuideModal(true)}
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              minWidth: 0, // Remove MUI's default padding
            }}
          >
            <InfoOutlineIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Box>
      </Box>
      <Paper
        elevation={3}
        sx={{
          paddingBottom: 0,
          maxWidth: '100%',
          boxSizing: 'border-box',
          mx: 'auto',
          backgroundColor: theme.palette.background.paper,
          borderRadius: '16px',
        }}
      >
        <CustomTableScrollbar sx={{ width: '100%', overflowX: 'auto' }}>
          <TableContainer sx={{ width: '100%', borderRadius: '16px', overflow: 'hidden' }}>
  <Table
    sx={{
      tableLayout: 'fixed',
      width: '100%',
      '& .MuiTableHead-root .MuiTableCell-root': {
        fontWeight: 'bold',
        backgroundColor: theme.palette.primary.main,
        color: 'white',
      },
    }}
  >
    <TableHead>
      <TableRow>
        <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>S.No</TableCell>
        <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', width: '18%' }}>User Role Name</TableCell>
        <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', width: '22%' }}>Name</TableCell>
        <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', width: '20%' }}>User Name</TableCell>
        <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', width: '20%' }}>Password</TableCell>
        <TableCell sx={{ fontWeight: 'bold', fontSize: '1.1rem', width: '15%' }}>Expiry Date</TableCell>
        <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.dark, fontSize: '1.1rem', width: '15%' }}>Action</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {paginatedData.length > 0 ? (
        paginatedData.map((user, index) => (
          <TableRow
            key={user.UserId}
            sx={{
              backgroundColor: index % 2 === 0 ? theme.palette.background.paper : theme.palette.background.paper,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <TableCell sx={{ fontSize: '1rem' }}>{(page - 1) * rowsPerPage + index + 1}</TableCell>
            <TableCell sx={{ fontSize: '1rem', wordBreak: 'break-word' }}>{user.UserRoleName}</TableCell>
            <TableCell>
  {user.EmployeeName !== 'Unknown' ? user.EmployeeName : user.student_name}
</TableCell>
            <TableCell sx={{ fontSize: '1rem', wordBreak: 'break-word' }}>{user.UserName}</TableCell>
     <TableCell sx={{ fontSize: '1rem', wordBreak: 'break-word' }}>
  <Box component="span" display="inline-flex" alignItems="center" gap={0.5}>
    {passwordVisibility[user.UserId] && user.Password
      ? user.Password                     // only show if we actually have it
      : '••••••••'}

    {user.Password ? (                    // only show clickable eye if we have password
      <IconButton
        onClick={() => togglePasswordVisibility(user.UserId)}
        size="small"
        sx={{ p: 0, ml: 0.5, color: "#6e6e6eff" }}
      >
        {passwordVisibility[user.UserId]
          ? <VisibilityOff fontSize="small" />
          : <Visibility fontSize="small" />}
      </IconButton>
    ) : (
      <IconButton
        size="small"
        sx={{ p: 0, ml: 0.5, color: "#6e6e6eff", opacity: 0.4 }}
        disabled
      >
        <VisibilityOff fontSize="small" />
      </IconButton>
    )}
  </Box>
</TableCell>

            <TableCell sx={{ fontSize: '1rem', wordBreak: 'break-word' }}>{user.ExpiryDate}</TableCell>
            <TableCell align="center">
              <IconButton
                onClick={() => {
                  // Navigate to student edit page if role is student, else regular edit
                  const roleName = String(user.UserRoleName || '').toLowerCase();
                  if (roleName === 'student') {
                    navigate(`/edit-student-user/${user.UserId}`);
                  } else {
                    navigate(`/edit-user/${user.UserId}`);
                  }
                }}
                sx={{
                  color: theme.palette.primary.light,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                color="error"
                onClick={() => handleShowDeleteModal(user)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </TableCell>
          </TableRow>
        ))
      ) : (
        <TableRow>
          <TableCell colSpan={7} align="center" sx={{ fontSize: '1rem', padding: '20px' }}>
            No Data Found
          </TableCell>
        </TableRow>
      )}
    </TableBody>
  </Table>
</TableContainer>

        </CustomTableScrollbar>
      </Paper>

      {filteredData.length > 0 && (
        <Box
          sx={{
            maxWidth: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            p: 2,
            mx: 'auto',
            pt: 2,
          }}
        >
          <Typography variant="body2" color={theme.palette.primary.main}>
            Page <strong>{page}</strong> of <strong>{Math.ceil(filteredData.length / rowsPerPage)}</strong>
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 1}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronLeft />
            </IconButton>
            {[...Array(Math.ceil(filteredData.length / rowsPerPage))].map((_, index) => (
              <IconButton
                key={index + 1}
                onClick={() => handleChangePage(null, index + 1)}
                sx={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: page === index + 1 ? theme.palette.primary.main : 'transparent',
                  color: page === index + 1 ? theme.palette.primary.contrastText : theme.palette.text.primary,
                  '&:hover': {
                    backgroundColor: page === index + 1 ? theme.palette.primary.dark : theme.palette.action.hover,
                  },
                }}
              >
                {index + 1}
              </IconButton>
            ))}
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage)}
              sx={{
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
                '&.Mui-disabled': {
                  opacity: 0.3,
                },
                borderRadius: '50%',
                width: '36px',
                height: '36px',
              }}
            >
              <ChevronRight />
            </IconButton>
            <FormControl size="small" sx={{ minWidth: 100, ml: 1 }}>
              <InputLabel id="rows-per-page-label" sx={{ color: theme.palette.text.primary }}>Rows</InputLabel>
              <Select
                labelId="rows-per-page-label"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                label="Rows"
                sx={{
                  height: '36px',
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.text.primary,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                {rowsPerPageOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Modal */}
      <Modal
        open={showDeleteModal}
        onClose={handleCloseDeleteModal}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="delete-modal-title" variant="h6" sx={{ color: '#fff' }}>
              Delete Confirmation ⚠️
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="delete-modal-description" sx={{ mb: 2 }}>
              Are you sure you  want to delete <strong>{selectedUser?.EmployeeName}</strong>?
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ mr: 1 }} data-app-cancel="true">
                Cancel
              </Button>
             <Button 
  variant="contained" 
  color="error" 
  onClick={handleDelete}
  disabled={deleting}
>
  {deleting ? 'Deleting...' : 'Yes, Delete'}
</Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Add User Modal */}
      <Modal
        open={showAddUserModal}
        onClose={() => setShowAddUserModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <CustomTableScrollbar
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Add User
            </Typography>
            <IconButton onClick={() => setShowAddUserModal(false)} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          {/* <Box sx={{ padding: '20px', overflowY: 'auto', flexGrow: 1 }}>
            <form onSubmit={handleSave}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="userRole-label" sx={{ color: theme.palette.text.primary }}>
                  User Role
                </InputLabel>
                <Select
                  labelId="userRole-label"
                  id="userRole"
                  name="userRole"
                  value={formData.userRole}
                  label="User Role"
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.text.primary,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  {staticUserRoles.map((role) => (
                    <MenuItem key={role.roleId} value={role.roleName}>
                      {role.roleName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Employee Name"
                variant="outlined"
                fullWidth
                name="employeeName"
                value={formData.employeeName}
                onChange={handleChange}
                sx={{
                  mb: 3,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="User Name"
                variant="outlined"
                fullWidth
                name="userName"
                value={formData.userName}
                onChange={handleChange}
                sx={{
                  mb: 3,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                name="password"
                value={formData.password}
                onChange={handleChange}
                error={passwordMatchError}
                helperText={passwordMatchError ? "Passwords do not match" : ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPassword} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={passwordMatchError}
                helperText={passwordMatchError ? "Passwords do not match" : ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowConfirmPassword} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="Expiry Date"
                type="date"
                variant="outlined"
                fullWidth
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
                sx={{
                  mb: 3,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Tooltip
                  title={
                    !formData.userRole
                      ? 'Select User Role'
                      : !formData.employeeName
                        ? 'Enter Employee Name'
                        : !formData.userName
                          ? 'Enter User Name'
                          : !formData.password
                            ? 'Enter Password'
                            : !formData.confirmPassword
                              ? 'Enter Confirm Password'
                              : !formData.expiryDate
                                ? 'Enter Expiry Date'
                                : ''
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      sx={{
                        mr: 2,
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': { backgroundColor: theme.palette.primary.dark },
                      }}
                      disabled={
                        !formData.userRole ||
                        !formData.employeeName ||
                        !formData.userName ||
                        !formData.password ||
                        !formData.confirmPassword ||
                        !formData.expiryDate
                      }
                    >
                      SAVE
                    </Button>
                  </span>
                </Tooltip>
                <Button variant="contained" color="error" onClick={handleReset}>
                  RESET
                </Button>
              </Box>
            </form>
          </Box> */}
        </CustomTableScrollbar>
      </Modal>

      {/* Edit User Modal */}
      {/* <Modal
        open={showEditUserModal}
        onClose={handleCancel}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <CustomTableScrollbar
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              padding: '12px 16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            }}
          >
            <Typography variant="h6" sx={{ color: '#fff', fontWeight: 'bold' }}>
              Edit User
            </Typography>
            <IconButton onClick={handleCancel} sx={{ color: '#fff' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ padding: '20px', overflowY: 'auto', flexGrow: 1 }}>
            <form onSubmit={handleUpdate}>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel id="userRole-label" sx={{ color: theme.palette.text.primary }}>
                  User Role
                </InputLabel>
                <Select
                  labelId="userRole-label"
                  id="userRole"
                  name="userRole"
                  value={formData.userRole}
                  label="User Role"
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.text.primary,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  {staticUserRoles.map((role) => (
                    <MenuItem key={role.roleId} value={role.roleName}>
                      {role.roleName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Employee Name"
                variant="outlined"
                fullWidth
                name="employeeName"
                value={formData.employeeName}
                onChange={handleChange}
                sx={{
                  mb: 3,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="User Name"
                variant="outlined"
                fullWidth
                name="userName"
                value={formData.userName}
                onChange={handleChange}
                sx={{
                  mb: 3,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                name="password"
                value={formData.password}
                onChange={handleChange}
                error={passwordMatchError}
                helperText={passwordMatchError ? "Passwords do not match" : ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowPassword} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                variant="outlined"
                fullWidth
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={passwordMatchError}
                helperText={passwordMatchError ? "Passwords do not match" : ""}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClickShowConfirmPassword} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <TextField
                label="Expiry Date"
                type="date"
                variant="outlined"
                fullWidth
                name="expiryDate"
                value={formData.expiryDate}
                onChange={handleChange>
                InputLabelProps={{ shrink: true }}
                sx={{
                  mb: 3,
                  '& .MuiInputLabel-root': { color: theme.palette.text.primary },
                  '& .MuiInputLabel-root.Mui-focused': { color: theme.palette.primary.main },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.text.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Tooltip
                  title={
                    !formData.userRole
                      ? 'Select User Role'
                      : !formData.employeeName
                        ? 'Enter Employee Name'
                        : !formData.userName
                          ? 'Enter User Name'
                          : !formData.password
                            ? 'Enter Password'
                            : !formData.confirmPassword
                              ? 'Enter Confirm Password'
                              : !formData.expiryDate
                                ? 'Enter Expiry Date'
                                : ''
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="contained"
                      onClick={handleUpdate}
                      sx={{
                        mr: 2,
                        backgroundColor: theme.palette.primary.main,
                        '&:hover': { backgroundColor: theme.palette.primary.dark },
                      }}
                      disabled={
                        !formData.userRole ||
                        !formData.employeeName ||
                        !formData.userName ||
                        !formData.password ||
                        !formData.confirmPassword ||
                        !formData.expiryDate
                      }
                    >
                      UPDATE
                    </Button>
                  </span>
                </Tooltip>
                <Button variant="contained" color="error" onClick={handleCancel}>
                  CANCEL
                </Button>
              </Box>
            </form>
          </Box>
        </CustomTableScrollbar>
      </Modal> */}

      {/* Success Modal */}
      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ backgroundColor: theme.palette.primary.main, padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              {successModalType === 'add' ? 'User Added ✅' : 'User Updated ✅'}
            </Typography>
          </Box>
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              {successModalType === 'add' ? 'User Added Successfully' : 'User Updated Successfully'}
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => setShowSuccessModal(false)}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Modal
  open={showUserGuideModal}
  onClose={() => setShowUserGuideModal(false)}
  BackdropProps={{ style: { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }}
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 800,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box
      sx={{
        backgroundColor: theme.palette.primary.main,
        padding: '12px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        position: 'relative',
      }}
    >
      <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
        User Module - For The User Guide
      </Typography>
      <IconButton
        onClick={() => setShowUserGuideModal(false)}
        sx={{ color: '#fff', position: 'absolute', right: 14 }}
      >
        <CloseIcon />
      </IconButton>
    </Box>

    {/* Scrollable Content */}
  <CustomTableScrollbar sx={{ maxHeight: '70vh', overflowY: 'auto', padding: '20px' }}>
  {/* Section 1: Add User */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mb: 1 }}
  >
    ➤ Follow the steps below to add a new user:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8', cursor: 'pointer' }}>
    <li>Click the <strong>ADD USER</strong> button at the top right to open the add user form.</li>
    <li>Enter the Employee Name, User Name (in email format), Password, Confirm Password, and Expiry Date.</li>
    <li>Ensure the Password and Confirm Password fields match.</li>
    <li>Click the <strong>SAVE</strong> button to add the user. A success message will confirm the addition.</li>
    <li>The new user record will appear in the User List table.</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: All fields are required. Passwords must match, and the User Name must be a valid email address.
  </Typography>

  {/* Section 2: Edit User */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to edit an existing user:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
    <li>Select the user in the User List table whose record you want to update.</li>
    <li>Click the <strong>Edit</strong> (pencil icon) in the Action column to open the edit user form.</li>
    <li>Ensure the Password and Confirm Password fields match.</li>
    <li>Click the <strong>UPDATE</strong> button to save changes. A success message will confirm the update.</li>
    <li>Alternatively, click <strong>CANCEL</strong> to discard changes and close the form.</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: All fields are required, and passwords must match to avoid submission errors.
  </Typography>

  {/* Section 3: Delete User */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to delete a user:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
    <li>Select the user in the User List table whose record you want to delete.</li>
    <li>Click the <strong>Delete</strong> (trash icon) in the Action column.</li>
    <li>Confirm the deletion in the modal by clicking <strong>Yes, Delete</strong>.</li>
    <li>The user record will be removed, and a success message will appear.</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Deleted users cannot be recovered. Proceed with caution.
  </Typography>

  {/* Section 4: Search Users */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to search for a user:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
    <li>Use the search bar at the top left of the User List.</li>
    <li>Enter the user's role, employee name, username, or expiry date to filter the table.</li>
    <li>The table will update automatically to show matching users.</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: The search is case-insensitive and filters by User Role, Employee Name, User Name, or Expiry Date.
  </Typography>

  {/* Section 5: Password Visibility */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to toggle password visibility:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
    <li>In the Add or Edit User modal, click the eye icon next to the Password or Confirm Password field.</li>
    <li>This toggles the password visibility between text and hidden (dots) for easier verification.</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: Use this feature to verify passwords, but always protect sensitive information.
  </Typography>

  {/* Section 6: Pagination */}
  <Typography
    variant="body1"
    sx={{ fontWeight: 'bold', color: theme.palette.primary.main, mt: 3, mb: 1 }}
  >
    ➤ Follow these steps to navigate the user list:
  </Typography>
  <ul style={{ marginLeft: '20px', lineHeight: '1.8' }}>
    <li>Use the pagination controls at the bottom to navigate through pages.</li>
    <li>Click the left (<strong>&lt;</strong>) or right (<strong>&gt;</strong>) arrows to move between pages.</li>
    <li>Select a specific page number by clicking on it.</li>
    <li>Adjust the number of rows per page using the dropdown menu (e.g., 5, 10, 25, 50 rows).</li>
  </ul>
  <Typography variant="body2" sx={{ mt: 2, color: theme.palette.text.primary }}>
    ⚠️ Note: The table displays only the selected number of rows per page.
  </Typography>

  {/* Bottom Close Button */}
  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
    {/* Close button code can be uncommented and used as needed */}
  </Box>
</CustomTableScrollbar>

  </Box>
</Modal>
    </Container>
  );
};

export default User;